import { motion } from "framer-motion";
import { Award, Briefcase, GraduationCap, Target, Zap, ChevronRight, Shield, Users, MapPin, Tv, BookOpen, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const credentials = [
  { label: "FCRR", value: "Federal" },
  { label: "RPR", value: "31609" },
  { label: "California CSR", value: "14469" },
  { label: "Louisiana CCR", value: "27019" },
  { label: "Tennessee CSR", value: "968" },
  { label: "CRC", value: "Realtime Captioner" },
  { label: "CRI", value: "Reporting Instructor" },
  { label: "CPE", value: "Program Evaluator" },
  { label: "FL Notary", value: "Commissioned" },
];

const teachingPoints = [
  "What it takes to reach speed",
  "How to build accuracy under pressure",
  "Where most students struggle",
];

const emphasisPoints = [
  { icon: Target, text: "Pattern recognition" },
  { icon: Zap, text: "Repetition and consistency" },
  { icon: ChevronRight, text: "Building speed the right way" },
  { icon: Shield, text: "Learning to perform in real time" },
];

const programDesign = [
  "Simplify the path into court reporting",
  "Focus on the actual skill required",
  "Give students a clear, structured way to progress",
];

export default function AboutPage() {
  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20">

      {/* Hero Section */}
      <section className="py-24 md:py-36 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-[0%] left-[0%] w-[800px] h-[800px] bg-[#38b6ff]/15 rounded-full blur-[220px]"></div>
        <div className="absolute bottom-[5%] right-[5%] w-[600px] h-[600px] bg-[#1a5aaa]/10 rounded-full blur-[180px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-6"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#38b6ff] rounded-full animate-pulse-glow"></span>
                  About the Founder
                </span>
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-[#F5F5F7] mb-4"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
                data-testid="about-heading"
              >
                Evangeline <span className="gradient-text">Aymond</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="text-lg md:text-xl text-[#E0E0E0] mb-4"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Founder &amp; Program Director, StenoU
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="text-[#B0B0B0] leading-relaxed max-w-xl"
              >
                Over two decades in the court reporting profession, building a career grounded in precision, consistency, and real-world experience.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-[#38b6ff]/30 to-transparent rounded-2xl blur-sm"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_stenography-careers/artifacts/oijpunvf_20260411_164147.jpg"
                  alt="Evangeline Aymond — Founder & Program Director, StenoU"
                  className="relative w-72 sm:w-80 lg:w-96 aspect-square object-cover object-top rounded-2xl border border-[#333333]"
                  data-testid="founder-photo"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-20 md:py-28 bg-section-dark" data-testid="credentials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Credentials
              </p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                Nationally <span className="gradient-text">Credentialed</span>
              </h2>
              <p className="text-[#E0E0E0] leading-relaxed mb-8">
                Evangeline is a federally certified and nationally credentialed court reporter, holding multiple state licenses and certifications across California, Louisiana, Tennessee, and Florida. Her work has spanned depositions, arbitrations, mediations, and trials across multiple jurisdictions.
              </p>
              <p className="text-[#E0E0E0] leading-relaxed">
                Throughout her career, she has developed a reputation for producing clean, accurate transcripts and maintaining a strong command of the record&mdash;even in complex and fast-paced proceedings.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-3 gap-3">
                {credentials.map((cred, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="glass-card p-5 text-center"
                    data-testid={`credential-${i}`}
                  >
                    <Award className="w-5 h-5 text-[#38b6ff] mx-auto mb-3" />
                    <p className="text-xs text-[#B0B0B0] mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{cred.label}</p>
                    <p className="text-sm font-bold text-[#F5F5F7]">{cred.value}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 glass-card p-5 flex items-center gap-4">
                <MapPin className="w-5 h-5 text-[#38b6ff] flex-shrink-0" />
                <p className="text-sm text-[#E0E0E0]">
                  Licensed across <span className="text-[#F5F5F7] font-semibold">California, Louisiana, Tennessee, and Florida</span> with multi-jurisdiction experience
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education & Career History */}
      <section className="py-20 md:py-28 bg-[#0D0D0D] relative overflow-hidden" data-testid="career-section">
        <div className="absolute top-[10%] right-[5%] w-[500px] h-[500px] bg-[#38b6ff]/10 rounded-full blur-[200px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

            {/* Education */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Education
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F5F5F7] mb-8" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Academic <span className="gradient-text">Foundation</span>
              </h2>
              <div className="space-y-6">
                {[
                  { school: "Colorado Technical University", degree: "B.S. in Applied Sciences of Court Reporting", date: "June 2012", icon: GraduationCap },
                  { school: "Court Reporting Institute of Louisiana", degree: "Certificate of Completion", date: "May 2007", icon: BookOpen },
                ].map((edu, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="glass-card p-6"
                    data-testid={`education-${i}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#38b6ff]/10 border border-[#38b6ff]/30 flex items-center justify-center flex-shrink-0 rounded-lg">
                        <edu.icon className="w-5 h-5 text-[#38b6ff]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#F5F5F7]">{edu.school}</p>
                        <p className="text-sm text-[#E0E0E0]">{edu.degree}</p>
                        <p className="text-xs text-[#B0B0B0] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{edu.date}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Career Timeline */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Career
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F5F5F7] mb-8" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Professional <span className="gradient-text">Timeline</span>
              </h2>
              <div className="space-y-6">
                {[
                  {
                    role: "Stenography Educator",
                    period: "2005 - Present",
                    icon: GraduationCap,
                    details: ["Court Reporting", "CART & Closed Captioning", "Scoping & Proofreading"],
                  },
                  {
                    role: "Stenographic Court Reporter",
                    period: "2007 - Present",
                    icon: Briefcase,
                    details: ["Realtime, rough drafts, expedites", "AAA/FINRA & labor law arbitrations", "Pharmaceutical & medical malpractice", "Civil trials, hearings, conferences", "Complex business litigation", "Maritime & energy"],
                  },
                  {
                    role: "Broadcast Captioner / CART Provider",
                    period: "2014 - Present",
                    icon: Tv,
                    details: ["Local news, politics, weather", "Sports: football, baseball, basketball, volleyball, hockey, soccer, golf, tennis, horse racing", "Undergraduate & graduate academic classes", "Meetings, seminars, and conferences (domestic & global)"],
                  },
                ].map((job, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="glass-card p-6"
                    data-testid={`career-${i}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#38b6ff]/10 border border-[#38b6ff]/30 flex items-center justify-center flex-shrink-0 rounded-lg">
                        <job.icon className="w-5 h-5 text-[#38b6ff]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-bold text-[#F5F5F7]">{job.role}</p>
                          <span className="text-xs text-[#38b6ff]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{job.period}</span>
                        </div>
                        <ul className="space-y-1">
                          {job.details.map((d, j) => (
                            <li key={j} className="text-xs text-[#B0B0B0] flex items-start gap-2">
                              <span className="w-1 h-1 bg-[#38b6ff] rounded-full mt-1.5 flex-shrink-0"></span>
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* 99% Translation Rate Badge */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-6 glass-card p-5 flex items-center gap-4"
                data-testid="translation-rate"
              >
                <div className="w-10 h-10 bg-[#38b6ff]/10 border border-[#38b6ff]/30 flex items-center justify-center flex-shrink-0 rounded-lg">
                  <Target className="w-5 h-5 text-[#38b6ff]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#F5F5F7]">99% Average Translation Rate</p>
                  <p className="text-xs text-[#B0B0B0]">Caption Masters &middot; September 2014</p>
                </div>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 md:py-28 bg-[#0D0D0D] relative overflow-hidden" data-testid="experience-section">
        <div className="absolute top-[10%] right-[5%] w-[500px] h-[500px] bg-[#38b6ff]/10 rounded-full blur-[200px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Beyond the Classroom
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Experience That <span className="gradient-text">Goes Beyond</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-10 md:p-14 mb-12"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#38b6ff]/10 border border-[#38b6ff]/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-7 h-7 text-[#38b6ff]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-[#F5F5F7]" style={{ fontFamily: 'Unbounded, sans-serif' }}>10+ Years Teaching</p>
                  <p className="text-sm text-[#B0B0B0]">Court reporting students &amp; scopist training</p>
                </div>
              </div>
              <p className="text-[#E0E0E0] leading-relaxed mb-8">
                Before launching StenoU, Evangeline spent more than 10 years teaching court reporting students and training scopists, helping others develop the same skill set that built her career.
              </p>
              <p className="text-[#E0E0E0] leading-relaxed mb-6">
                She understands firsthand:
              </p>
              <div className="space-y-3">
                {teachingPoints.map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#38b6ff] rounded-full flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#F5F5F7]">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="text-lg text-[#E0E0E0] italic leading-relaxed max-w-3xl mx-auto">
                Her teaching is rooted in real-world application, not theory. Every concept is tied back to what actually happens on the job.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skill-Based Approach */}
      <section className="py-20 md:py-28 bg-section-alt" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Philosophy
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                A Career Built on <span className="gradient-text">Skill</span>
              </h2>
              <p className="text-[#E0E0E0] leading-relaxed mb-8">
                Court reporting is not a memorization-based field&mdash;it is a skill-based profession. Evangeline&apos;s approach reflects that.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {emphasisPoints.map((point, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="glass-card flex items-center gap-3 p-4"
                  >
                    <point.icon className="w-5 h-5 text-[#38b6ff]" />
                    <span className="text-[#F5F5F7] text-sm">{point.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-10 md:p-12"
            >
              <div className="border-l-2 border-[#38b6ff] pl-6">
                <p className="text-xl md:text-2xl text-[#F5F5F7] font-bold leading-snug mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                &ldquo;Learning steno isn&apos;t about perfection. It&apos;s about willpower and consistency.&rdquo;
                </p>
                <p className="text-sm text-[#38b6ff]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  &mdash; Evangeline Aymond
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why StenoU Exists */}
      <section className="py-20 md:py-28 bg-[#0D0D0D] relative overflow-hidden" data-testid="why-stenou-section">
        <div className="absolute bottom-[5%] left-[5%] w-[600px] h-[600px] bg-[#38b6ff]/8 rounded-full blur-[200px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                The Mission
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Why <span className="gradient-text">StenoU</span> Exists
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
              {[
                { icon: Users, text: "Students overwhelmed by the process" },
                { icon: Target, text: "Unclear expectations" },
                { icon: Briefcase, text: "Inconsistent training methods" },
                { icon: GraduationCap, text: "A lack of practical, skill-focused instruction" },
              ].map((problem, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="glass-card p-5 flex items-center gap-4"
                >
                  <problem.icon className="w-5 h-5 text-[#B0B0B0] flex-shrink-0" />
                  <span className="text-[#E0E0E0] text-sm">{problem.text}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <p className="text-lg text-[#F5F5F7] font-semibold mb-6 text-center">
                StenoU was created to fix that.
              </p>
              <p className="text-[#E0E0E0] mb-8 text-center">The program is designed to:</p>
              <div className="flex flex-col items-start gap-4 max-w-md mx-auto">
                {programDesign.map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#38b6ff] rounded-full flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#F5F5F7]">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom Line */}
      <section className="py-20 md:py-28 bg-section-dark" data-testid="bottom-line-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                A Different Kind of Program Director
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                The <span className="gradient-text">Bottom Line</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-10 md:p-14 mb-12"
            >
              <p className="text-[#E0E0E0] leading-relaxed mb-6">
                Evangeline is not removed from the profession&mdash;she built her career in it. She understands the pressure of real-time writing, the expectations of attorneys and agencies, and the level of performance required to succeed.
              </p>
              <p className="text-[#E0E0E0] leading-relaxed mb-6">
                That perspective shapes everything inside StenoU.
              </p>
              <div className="border-l-2 border-[#38b6ff] pl-6 py-2 mb-6">
                <p className="text-[#F5F5F7] text-lg leading-relaxed">
                  Students are not learning from someone who <em>studied</em> court reporting.
                </p>
                <p className="text-[#F5F5F7] text-lg leading-relaxed mt-2">
                  They are learning from someone who has <strong>done it</strong>&mdash;at a high level, for years. And who knows exactly what it takes to get there.
                </p>
              </div>
              <p className="text-[#E0E0E0] leading-relaxed">
                If you&apos;re considering this path, the goal isn&apos;t just to learn it. <span className="text-[#F5F5F7] font-semibold">The goal is to be able to do it.</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personal Section */}
      <section className="py-20 md:py-28 bg-[#0D0D0D] relative overflow-hidden" data-testid="personal-section">
        <div className="absolute bottom-[10%] right-[10%] w-[500px] h-[500px] bg-[#38b6ff]/6 rounded-full blur-[180px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Beyond the Record
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Off the <span className="gradient-text">Clock</span>
              </h2>
              <p className="text-[#E0E0E0] leading-relaxed mb-4">
                When she&apos;s not working, Evangeline is usually spending time with her family&mdash;especially her children&mdash;or planning her next trip.
              </p>
              <p className="text-[#E0E0E0] leading-relaxed mb-4">
                She enjoys traveling, trying new places, and finding really good restaurants along the way.
              </p>
              <p className="text-[#E0E0E0] leading-relaxed">
                She&apos;s always open to great recommendations, whether it&apos;s a destination or a restaurant worth trying.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-[#38b6ff]/20 to-transparent rounded-2xl blur-sm"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_stenography-careers/artifacts/np5r424o_20260410_190506.jpg"
                  alt="Evangeline Aymond with her family"
                  className="relative w-full max-w-md aspect-square object-cover rounded-2xl border border-[#333333]"
                  data-testid="family-photo"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0D0D0D] relative overflow-hidden" data-testid="about-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#38b6ff]/5 rounded-full blur-[150px]"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-6" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Ready to Start?
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Learn From Someone Who&apos;s <span className="gradient-text">Been There</span>
          </h2>
          <p className="text-[#E0E0E0] leading-relaxed mb-10 max-w-2xl mx-auto">
            Book a free consultation with Evangeline and find out if court reporting is the right path for you.
          </p>
          <Link to="/stenou#consultation">
            <Button
              size="lg"
              className="rounded-none bg-[#38b6ff] text-[#0D0D0D] hover:bg-[#38b6ff]/90 btn-neon text-base px-8 py-6 font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid="about-cta-button"
            >
              <Zap className="w-5 h-5 mr-2" />
              Book Your Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
